import React, { useEffect } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useFfpDashboardQuery } from '@api/useFfpDashboardQuery'
import { track } from '@utils/tracking'
import { Dashboard } from './components/Dashboard'
import { LoadError } from './components/LoadError'
import { Loading } from './components/Loading'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      useErrorBoundary: false,
    },
  },
})

type Props = {
  organization: { id: number }
  // The sales dashboard's incognito mode: the company isn't named.
  incognito?: boolean
}

export const FfpDashboard = ({ organization, incognito = false }: Props) => (
  <QueryClientProvider client={queryClient}>
    <Page organizationId={organization.id} incognito={incognito} />
  </QueryClientProvider>
)

const Page = ({ organizationId, incognito }: { organizationId: number; incognito: boolean }) => {
  const { data, status, refetch } = useFfpDashboardQuery(organizationId)

  useEffect(() => {
    track('FFP Dashboard: View')
  }, [])

  const onRetry = () => {
    track('FFP Dashboard: Retry load')
    refetch()
  }

  if (status === 'loading') return <Loading />
  // Not thrown: a failed request is not a crash, so it shouldn't reach the
  // switch's error boundary, which reports to Sentry and forgets the choice.
  if (status === 'error') return <LoadError onRetry={onRetry} />

  return <Dashboard data={data} incognito={incognito} />
}
